/**
 * sessionStorage-backed cache for geocoding responses, keyed by query string.
 *
 * Mapbox bills per Geocoding API request — caching identical queries within a
 * session avoids re-billing when the user types the same thing twice, focuses
 * the input again, or navigates back to a page that re-triggers the search.
 *
 * Each namespace gets its own storage key. Entries carry a timestamp and are
 * evicted oldest-first once the namespace exceeds CACHE_MAX_ENTRIES.
 */

const STORAGE_KEY_PREFIX = 'cocopool:geocodeCache:';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const CACHE_MAX_ENTRIES = 100;

const storageKey = namespace => `${STORAGE_KEY_PREFIX}${namespace}`;

const safeRead = namespace => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return {};
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey(namespace));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const safeWrite = (namespace, cache) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    window.sessionStorage.setItem(storageKey(namespace), JSON.stringify(cache));
  } catch {
    /* quota or serialization issue — fine to skip */
  }
};

export const getCachedGeocode = (namespace, key) => {
  const cache = safeRead(namespace);
  const entry = cache[key];
  if (!entry) {
    return null;
  }
  if (Date.now() - entry.t > CACHE_TTL_MS) {
    return null;
  }
  return entry.v;
};

export const setCachedGeocode = (namespace, key, value) => {
  const cache = safeRead(namespace);
  cache[key] = { t: Date.now(), v: value };
  const keys = Object.keys(cache);
  if (keys.length > CACHE_MAX_ENTRIES) {
    const sorted = keys.sort((a, b) => (cache[a].t || 0) - (cache[b].t || 0));
    const excess = sorted.length - CACHE_MAX_ENTRIES;
    for (let i = 0; i < excess; i++) {
      delete cache[sorted[i]];
    }
  }
  safeWrite(namespace, cache);
};

export const normalizeCacheKey = query => (query || '').trim().toLowerCase();
